import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import type { MedicalRecord } from '../../../../types/animal';

interface MedicalRecordDialogProps {
  open: boolean;
  onClose: () => void;
  newMedicalRecord: Partial<MedicalRecord>;
  setNewMedicalRecord: React.Dispatch<React.SetStateAction<Partial<MedicalRecord>>>;
  handleSaveMedicalRecord: () => void;
  medicalRecordTypes: string[];
}

const MedicalRecordDialog: React.FC<MedicalRecordDialogProps> = ({
  open,
  onClose,
  newMedicalRecord,
  setNewMedicalRecord,
  handleSaveMedicalRecord,
  medicalRecordTypes,
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add Medical Record</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Date"
              value={newMedicalRecord.date || ''}
              onChange={e => setNewMedicalRecord({ ...newMedicalRecord, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Record Type"
              value={newMedicalRecord.type || 'routine'}
              onChange={e => setNewMedicalRecord({ ...newMedicalRecord, type: e.target.value as any })}
              required
            >
              {medicalRecordTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              value={newMedicalRecord.title || ''}
              onChange={e => setNewMedicalRecord({ ...newMedicalRecord, title: e.target.value })}
              required
              helperText="E.g., Deworming, Vaccination, Surgery, Health Check"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={newMedicalRecord.description || ''}
              onChange={e => setNewMedicalRecord({ ...newMedicalRecord, description: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Performed By"
              value={newMedicalRecord.performedBy || ''}
              onChange={e => setNewMedicalRecord({ ...newMedicalRecord, performedBy: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Status"
              value={newMedicalRecord.status || 'completed'}
              onChange={e => setNewMedicalRecord({ ...newMedicalRecord, status: e.target.value as any })}
              required
            >
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="ongoing">Ongoing</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Cost (Ksh)"
              value={newMedicalRecord.cost ?? ''}
              onChange={e => setNewMedicalRecord({ ...newMedicalRecord, cost: Number(e.target.value) })}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          {(newMedicalRecord.type === 'treatment' || newMedicalRecord.type === 'vaccination') && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Medications/Vaccines"
                value={newMedicalRecord.medications ? newMedicalRecord.medications.map(m => m.name).join(', ') : ''}
                onChange={e => setNewMedicalRecord({ ...newMedicalRecord, medications: e.target.value.split(',').map(m => ({ name: m.trim(), dosage: '', frequency: '' })) })}
                placeholder="Enter medications separated by commas"
              />
            </Grid>
          )}
          {newMedicalRecord.status === 'completed' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Result/Outcome"
                value={newMedicalRecord.result || ''}
                onChange={e => setNewMedicalRecord({ ...newMedicalRecord, result: e.target.value })}
                placeholder="Describe the outcome or result of this treatment/examination"
                multiline
                rows={2}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSaveMedicalRecord} 
          variant="contained" 
          color="primary"
          startIcon={<SaveIcon />}
          disabled={
            !newMedicalRecord.title ||
            !newMedicalRecord.description ||
            !newMedicalRecord.performedBy
          }
        >
          Save Record
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicalRecordDialog;
